// Example figures from the Business Impact prototype (Apex Co. and Morrison).
// They are not any company's data; later sections render them until real figures exist.

export type ValueDriverId =
  | "productivity"
  | "absenteeism"
  | "healthClaims"
  | "retention";

export type BusinessRiskId =
  | "workforceLoss"
  | "performanceEngagement"
  | "operationalRisk"
  | "healthClaims"
  | "workRelationships";

export type BandChange = { percent: number; employees: number };

export type DomainId = "movement" | "workCapacity" | "sleep";

export type StressBands = {
  high: number;
  moderate: number;
  low: number;
  employees: number;
};

type PriorityDomain = { id: DomainId; score: number };

export type SectorId = "legal";

// The page tells a different story at each stage of a client's partnership.
export type BusinessImpactVersion = "full" | "projected" | "pilot";

type StressChange = {
  high: BandChange;
  low: BandChange;
};

type Risk =
  | {
      kind: "specific";
      domains: [PriorityDomain, PriorityDomain];
      departments: [string, string];
    }
  // Too little data yet to name departments or score domains.
  | { kind: "general"; domains: [DomainId, DomainId] };

type RiskSection = {
  riskMultipliers: { id: BusinessRiskId; multiplier: number }[];
  risk: Risk;
};

// The figures a pilot has not produced yet, so a pilot sample cannot carry them.
type RoiSection = {
  roiMultiple: number;
  totalSavings: { amount: number; currency: string };
  valueDrivers: { id: ValueDriverId; amount: number; sharePercent: number }[];
  stress: { before: StressBands; after: StressBands };
  stressChange: StressChange;
  scenarioModeller: { currentParticipationPercent: number };
};

type Progress = {
  month: number;
  of: number;
  // Calendar month of the review, "YYYY-MM".
  review: string;
};

export type ProofPointId =
  | "activatedFirstTwoWeeks"
  | "baselinesEstablished"
  | "coachingWithOri";

export type FullSample = RiskSection &
  RoiSection & {
    version: "full";
    period: {
      // Calendar months, "YYYY-MM".
      from: string;
      to: string;
      nextCalculation: { year: number; quarter: 1 | 2 | 3 | 4 };
    };
  };

export type ProjectedSample = RiskSection &
  RoiSection & {
    version: "projected";
    // `totalSavings` is the savings to date, and `roiMultiple` a projection.
    basis: { months: number; sector: SectorId };
    progress: Progress;
  };

export type PilotSample = RiskSection & {
  version: "pilot";
  progress: Progress;
  // Calendar days, "YYYY-MM-DD".
  period: { from: string; to: string };
  // `percent` points are out of 100; `count` points are people.
  proofPoints: (
    | { id: "activatedFirstTwoWeeks" | "coachingWithOri"; percent: number }
    | { id: "baselinesEstablished"; count: number }
  )[];
};

// Full and projected both carry ROI figures; a pilot has none.
export type RoiSample = FullSample | ProjectedSample;

export type BusinessImpactSample = RoiSample | PilotSample;

const RISK_MULTIPLIERS: RiskSection["riskMultipliers"] = [
  { id: "workforceLoss", multiplier: 8 },
  { id: "performanceEngagement", multiplier: 4 },
  { id: "operationalRisk", multiplier: 11 },
  { id: "healthClaims", multiplier: 2.5 },
  { id: "workRelationships", multiplier: 7 },
];

const FULL: FullSample = {
  version: "full",
  roiMultiple: 4.2,
  totalSavings: { amount: 384000, currency: "GBP" },
  period: {
    from: "2025-01",
    to: "2025-12",
    nextCalculation: { year: 2027, quarter: 1 },
  },
  valueDrivers: [
    { id: "productivity", amount: 161280, sharePercent: 42 },
    { id: "absenteeism", amount: 99840, sharePercent: 26 },
    { id: "healthClaims", amount: 69120, sharePercent: 18 },
    { id: "retention", amount: 53760, sharePercent: 14 },
  ],
  stress: {
    before: { high: 288, moderate: 672, low: 480, employees: 1440 },
    after: { high: 210, moderate: 648, low: 582, employees: 1440 },
  },
  stressChange: {
    high: { percent: -27.1, employees: -78 },
    low: { percent: 21.3, employees: 102 },
  },
  riskMultipliers: RISK_MULTIPLIERS,
  risk: {
    kind: "specific",
    domains: [
      { id: "movement", score: 57.4 },
      { id: "workCapacity", score: 63.4 },
    ],
    departments: ["Investment Banking", "Risk & Compliance"],
  },
  scenarioModeller: { currentParticipationPercent: 65 },
};

const PROJECTED: ProjectedSample = {
  version: "projected",
  roiMultiple: 3.2,
  totalSavings: { amount: 118000, currency: "GBP" },
  basis: { months: 6, sector: "legal" },
  progress: { month: 6, of: 12, review: "2026-12" },
  valueDrivers: [
    { id: "productivity", amount: 53100, sharePercent: 45 },
    { id: "absenteeism", amount: 30680, sharePercent: 26 },
    { id: "healthClaims", amount: 20060, sharePercent: 17 },
    { id: "retention", amount: 14160, sharePercent: 12 },
  ],
  stress: {
    before: { high: 72, moderate: 95, low: 53, employees: 220 },
    after: { high: 60, moderate: 92, low: 68, employees: 220 },
  },
  stressChange: {
    high: { percent: -16.7, employees: -12 },
    low: { percent: 28.3, employees: 15 },
  },
  riskMultipliers: RISK_MULTIPLIERS,
  risk: { kind: "general", domains: ["movement", "sleep"] },
  scenarioModeller: { currentParticipationPercent: 9 },
};

const PILOT: PilotSample = {
  version: "pilot",
  progress: { month: 3, of: 12, review: "2027-05" },
  period: { from: "2026-04-24", to: "2026-08-01" },
  proofPoints: [
    { id: "activatedFirstTwoWeeks", percent: 70 },
    { id: "baselinesEstablished", count: 22 },
    { id: "coachingWithOri", percent: 51 },
  ],
  riskMultipliers: RISK_MULTIPLIERS,
  risk: { kind: "general", domains: ["movement", "sleep"] },
};

export const BUSINESS_IMPACT_SAMPLES: {
  full: FullSample;
  projected: ProjectedSample;
  pilot: PilotSample;
} = { full: FULL, projected: PROJECTED, pilot: PILOT };
